import React, { useState } from "react";

const CheckSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CATEGORY_COLORS = ["#6B46FF", "#00D4E4", "#10B981", "#F59E0B", "#EF4444"];

const APP_STORE_PLATFORMS = [
  "apple_app_store",
  "google_play_console",
  "roku_developer",
  "amazon_fire_tv",
  "lg_tv",
  "samsung_tv",
  "android_tv",
  "apple_tv",
  "vizio_tv"
];

const isPaymentGatewaySlug = (slug) => {
  if (!slug) return false;
  const lower = slug.toLowerCase();
  if (APP_STORE_PLATFORMS.includes(lower)) return false;
  const appStoreKeywords = ["store", "play", "console", "developer", "tv"];
  return !appStoreKeywords.some(keyword => lower.includes(keyword));
};

const resolvedYesNo = (key, formValues) => {
  const val = formValues[key];
  if (val === "yes" || val === "no") return val;
  const normalized = String(val || "").toLowerCase().trim();
  if (normalized === "yes" || normalized === "true") return "yes";
  if (normalized === "no" || normalized === "false") return "no";
  return "";
};

const AccessControlSection = ({
  section,
  formValues = {},
  fieldMeta = {},
  updateField,
  requestDelete,
  clearField,
  isSuperAdmin = false,
  focusedFieldKey = null,
}) => {
  const platforms = React.useMemo(() => (section?.subsections || []).map((sub, idx) => ({
    slug: sub.subsectionSlug,
    display_name: sub.subsectionName,
    color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
    questions: sub.questions || [],
  })), [section]);

  const [expanded, setExpanded] = useState(null);

  React.useEffect(() => {
    if (focusedFieldKey) {
      const platform = platforms.find((p) =>
        p.questions.some((q) => q.key === focusedFieldKey)
      );
      if (platform) {
        setExpanded(platform.slug);
      }
    }
  }, [focusedFieldKey, platforms]);

  const toggle = (slug) => setExpanded(expanded === slug ? null : slug);

  const isYes = (field) => {
    const key = field.key;
    return resolvedYesNo(key, formValues) === "yes";
  };

  const handleToggle = (field, platformSlug) => {
    if (isSuperAdmin) return;
    const current = isYes(field);
    if (current) {
      if (clearField) clearField(field.key);
      else updateField(field, "", true);
    } else {
      updateField(field, "yes", true);
    }
  };

  const fullyDone = platforms.filter((p) => p.questions.length > 0 && p.questions.every(isYes)).length;
  const inProgress = platforms.filter((p) => p.questions.some(isYes) && !p.questions.every(isYes)).length;


  if (!platforms.length) {
    return <div className="gob-empty-state">No access platforms found.</div>;
  }

  return (
    <div>
      <div className="gob-breadcrumb">
        <span>Publisher Channel</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="current">Access Control</span>
      </div>

      <div className="gob-page-head">
        <h1 className="gob-page-title">Access <span className="gradient">Control</span></h1>
        <p className="gob-page-sub">
          For each platform, create the developer account, then grant our team access. We verify on our side.
        </p>
      </div>

      <div className="hiw-box">
        <div className="hiw-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div className="hiw-text">
          <strong>How it works:</strong> For each platform, mark the steps you complete.
        </div>
        <div className="hiw-flow">
          <span className="hiw-pill you">You complete steps</span>
          <svg className="hiw-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span className="hiw-pill we">We verify</span>
        </div>
      </div>

      <div className="gob-stats">
        <div className="stat-card"><div className="stat-label">Total platforms</div><div className="stat-value">{platforms.length}</div></div>
        <div className="stat-card"><div className="stat-label">All steps done</div><div className="stat-value success">{fullyDone}</div></div>
        <div className="stat-card"><div className="stat-label">In progress</div><div className="stat-value warn">{inProgress}</div></div>
        <div className="stat-card"><div className="stat-label">Not started</div><div className="stat-value">{platforms.length - fullyDone - inProgress}</div></div>
      </div>

      <div className="platforms">
        {platforms.map((p) => {
          const isExpanded = expanded === p.slug;
          const allDone = p.questions.length > 0 && p.questions.every(isYes);
          const anyDone = p.questions.some(isYes);

          return (
            <div key={p.slug} className={`platform${isExpanded ? " expanded" : ""}`}>
              <div className="platform-head" onClick={() => toggle(p.slug)}>
                <div className="platform-icon" style={{ background: p.color }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "white" }}>
                    {p.display_name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="platform-name">{p.display_name}</div>
                  <div className="platform-meta">{p.questions.length} step{p.questions.length !== 1 ? "s" : ""}</div>
                </div>

                <div className="status-flow">
                  {p.questions.map((q, qi) => {
                    const done = isYes(q);
                    return (
                      <React.Fragment key={q.key}>
                        {qi > 0 && (
                          <svg className="status-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        )}
                        <span className={`status-pill${done ? " done" : " empty"}`}>
                          <span className="ring">{done && <CheckSvg />}</span>
                          {q.prompt || `Step ${qi + 1}`}
                        </span>
                      </React.Fragment>
                    );
                  })}
                </div>

                <svg className="platform-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {isExpanded && (
                <div className="platform-body">
                  {p.questions.map((q, qi) => {
                    const done = isYes(q);
                    const key = q.key;
                    const meta = fieldMeta[key] || {};
                    const hasValue = done || !!String(formValues[key] || "").trim();

                    return (
                      <div key={key} id={`q-row-${key}`} data-key={key} className={`step-card${done ? " done" : ""}`}>
                        <div className="step-head">
                          <div className="step-num">{qi + 1}</div>
                          <div className="step-info">
                            <div className="step-title">{q.prompt}</div>
                            {q.tooltip && <div className="step-sub">{q.tooltip}</div>}
                          </div>
                          <span className={`step-status${done ? " done" : " empty"}`}>
                            {done ? "Done" : "Pending"}
                          </span>
                        </div>
                        <div className="step-body">
                          {q.help_detail_html && (
                            <div style={{ fontSize: 13, color: "var(--gob-ink-2)", marginBottom: 12 }}
                              dangerouslySetInnerHTML={{ __html: q.help_detail_html }} />
                          )}
                          <div className="mark-row" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {!isSuperAdmin && (
                              <button
                                className={`mark-btn${done ? " done" : ""}`}
                                onClick={() => handleToggle(q, p.slug)}
                              >
                                <CheckSvg />
                                {done ? "Done — click to undo" : "Mark as done"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccessControlSection;
